"use client";

import { useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const steps = [{
  id: 1,
  label: "Identity",
  key: "identity"
}, {
  id: 2,
  label: "Business",
  key: "business"
}, {
  id: 3,
  label: "Financials",
  key: "financials"
}, {
  id: 4,
  label: "Review",
  key: "review"
}];
const serviceInterests = [{
  id: "credit_architecture",
  label: "Credit Architecture"
}, {
  id: "business_funding",
  label: "Business Funding"
}, {
  id: "personal_funding",
  label: "Personal Funding"
}, {
  id: "wealth_strategy",
  label: "Wealth Strategy"
}, {
  id: "mentoring",
  label: "Mentoring"
}, {
  id: "business_credit",
  label: "Business Credit"
}];
const revenueOptions = [{
  value: "pre_revenue",
  label: "Pre-revenue / Startup"
}, {
  value: "under_100k",
  label: "Under $100,000"
}, {
  value: "100k_500k",
  label: "$100,000 - $500,000"
}, {
  value: "above_500k",
  label: "Above $500,000"
}];
const discoverySources = ["Private Referral", "Industry Publication", "Strategic Partner", "Social Media", "Executive Event", "Other"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [messageLength, setMessageLength] = useState(0);
  const [selectedGenres, setSelectedGenres] = useState<Record<string, boolean>>({});
  const [formSnapshot, setFormSnapshot] = useState<Record<string, string>>({});
  const goNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };
  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };
  const handleGenreToggle = (id: string, checked: boolean) => {
    setSelectedGenres(prev => ({
      ...prev,
      [id]: checked
    }));
  };
  if (isSubmitted && !submitError) {
    return <Dialog open={true}>
        <DialogContent showCloseButton={false} className="bg-background border-border max-w-2xl rounded-md shadow-xl">
          <div className="py-12 text-center">
            <div className="mb-8 flex justify-center">
              <div className="h-px w-16 bg-primary" />
            </div>
            <h3 className="font-default text-3xl md:text-4xl text-foreground leading-tight mb-6">
              Application <span className="text-primary">RECEIVED</span>.
            </h3>
            <p className="font-default text-xl text-muted-foreground italic mb-8">
              {successMessage || "Our analysts will review your profile within 24 hours."}
            </p>
            <div className="mb-10 flex justify-center">
              <div className="h-px w-24 bg-primary animate-pulse" />
            </div>
            <Button onClick={() => {
            resetForm();
            setCurrentStep(0);
            setSelectedGenres({});
            setFormSnapshot({});
            setMessageLength(0);
          }} variant="ghost" className="font-mono text-xs uppercase tracking-widest border border-border hover:border-primary hover:text-primary rounded-md">
              Submit New Application
            </Button>
          </div>
        </DialogContent>
      </Dialog>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="mb-16 md:mb-20">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          {steps.map((step, i) => {
          const isActive = i === currentStep;
          const isCompleted = i < currentStep;
          return <div data-index={i} key={step.id} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-3">
                  <div className={`flex items-center justify-center size-8 md:size-10 border transition-colors rounded-md ${isActive ? "bg-primary border-primary text-primary-foreground" : isCompleted ? "bg-primary border-primary text-primary-foreground" : "bg-transparent border-border text-muted-foreground"}`}>
                    {isCompleted ? <Check className="size-4" /> : <span className="font-mono text-xs">
                        0{step.id}
                      </span>}
                  </div>
                  <div className="hidden md:block text-center">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                      Phase 0{step.id}
                    </div>
                    <div className={`font-mono text-xs uppercase tracking-widest ${isActive || isCompleted ? "text-foreground" : "text-muted-foreground"}`}>
                      {step.label}
                    </div>
                  </div>
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-px bg-border mx-2 md:mx-4 mt-0 md:mt-[-2rem]" />}
              </div>;
        })}
        </div>
      </div>

      <div className="min-h-[400px]">
        {currentStep === 0 && <div className="space-y-12">
            <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Please enter your full name"
          }
        }} render={({
          field
        }) => <FormItem>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                    § 01 / 02
                  </div>
                  <FormLabel className="font-default text-2xl md:text-3xl text-foreground leading-snug block mb-6">
                    What is your <span className="text-primary">LEGAL</span> name?
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Alexander Sterling" {...field} onChange={e => {
              field.onChange(e);
              setFormSnapshot(s => ({
                ...s,
                full_name: e.target.value
              }));
            }} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 rounded-md" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email_address" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email"
          }
        }} render={({
          field
        }) => <FormItem>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                    § 02 / 02
                  </div>
                  <FormLabel className="font-default text-2xl md:text-3xl text-foreground leading-snug block mb-6">
                    Your primary <span className="text-primary">EXECUTIVE</span> email?
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="a.sterling@benewealth.com" {...field} onChange={e => {
              field.onChange(e);
              setFormSnapshot(s => ({
                ...s,
                email_address: e.target.value
              }));
            }} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 rounded-md" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>}

        {currentStep === 1 && <div className="space-y-12">
            <FormField control={control} name="phone_number" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                    § 01 / 03
                  </div>
                  <FormLabel className="font-default text-2xl md:text-3xl text-foreground leading-snug block mb-6">
                    Direct <span className="text-primary">CONTACT</span> number?
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 000-0000" {...field} onChange={e => {
              field.onChange(e);
              setFormSnapshot(s => ({
                ...s,
                phone_number: e.target.value
              }));
            }} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 rounded-md" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="business_name" render={({
          field
        }) => <FormItem>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                    § 02 / 03
                  </div>
                  <FormLabel className="font-default text-2xl md:text-3xl text-foreground leading-snug block mb-6">
                    Registered <span className="text-primary">ENTITY</span> name?
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Sterling Holdings LLC" {...field} onChange={e => {
              field.onChange(e);
              setFormSnapshot(s => ({
                ...s,
                business_name: e.target.value
              }));
            }} className="bg-transparent border-0 border-b border-border px-0 h-12 text-lg text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 rounded-md" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="financial_goals" rules={{
          required: "Please share your objectives",
          minLength: {
            value: 10,
            message: "Please provide at least 10 characters"
          },
          maxLength: {
            value: 500,
            message: "Maximum 500 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                    § 03 / 03
                  </div>
                  <FormLabel className="font-default text-2xl md:text-3xl text-foreground leading-snug block mb-6">
                    Define your <span className="text-primary">STRATEGIC</span> goals.
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Describe your funding needs or credit architecture requirements..." {...field} maxLength={500} onChange={e => {
              field.onChange(e);
              setMessageLength(e.target.value.length);
              setFormSnapshot(s => ({
                ...s,
                financial_goals: e.target.value
              }));
            }} className="bg-transparent border-0 border-b border-border px-0 py-3 min-h-[120px] text-lg text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 resize-none rounded-md" />
                  </FormControl>
                  <div className="flex justify-between items-center mt-2">
                    <FormMessage />
                    <span className="font-mono text-xs text-muted-foreground ml-auto">
                      {messageLength} / 500
                    </span>
                  </div>
                </FormItem>} />
          </div>}

        {currentStep === 2 && <div className="space-y-12">
            <FormField control={control} name="service_interest" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                    § 01 / 03
                  </div>
                  <FormLabel className="font-default text-2xl md:text-3xl text-foreground leading-snug block mb-6">
                    Primary <span className="text-primary">SOLUTION</span> required?
                  </FormLabel>
                  <Select onValueChange={v => {
            field.onChange(v);
            setFormSnapshot(s => ({
              ...s,
              service_interest: v
            }));
          }} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full bg-transparent border-0 border-b border-border px-0 h-12 text-lg text-foreground focus:ring-0 focus:border-primary rounded-md">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-card border-border rounded-md">
                      <SelectItem value="Credit Architecture">Credit Architecture</SelectItem>
                      <SelectItem value="Business Funding">Business Funding</SelectItem>
                      <SelectItem value="Wealth Strategy">Wealth Strategy</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                § 01 / 03
              </div>
              <p className="font-default text-2xl md:text-3xl text-foreground leading-snug mb-8">
                Areas of <span className="text-primary">INTEREST</span>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-5">
                {serviceInterests.map((service, i) => <label data-index={i} key={service.id} htmlFor={`service-${service.id}`} className="flex items-center gap-4 cursor-pointer group">
                    <Checkbox id={`service-${service.id}`} checked={!!selectedGenres[service.id]} onCheckedChange={checked => {
                const isChecked = checked === true;
                handleGenreToggle(service.id, isChecked);
                const updated = {
                  ...selectedGenres,
                  [service.id]: isChecked
                };
                const selected = serviceInterests.filter(g => updated[g.id]).map(g => g.label).join(", ");
                setFormSnapshot(s => ({
                  ...s,
                  interests: selected
                }));
              }} className="size-5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary rounded-md" />
                    <span className="font-default text-lg text-foreground group-hover:text-primary transition-colors">
                      {service.label}
                    </span>
                  </label>)}
              </div>
            </div>

            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                § 03 / 03
              </div>
              <p className="font-default text-2xl md:text-3xl text-foreground leading-snug mb-8">
                Annual <span className="text-primary">REVENUE</span> range.
              </p>
              <RadioGroup onValueChange={v => setFormSnapshot(s => ({
            ...s,
            revenue: v
          }))} className="space-y-4">
                {revenueOptions.map((option, i) => <label data-index={i} key={option.value} htmlFor={`revenue-${option.value}`} className="flex items-center gap-4 cursor-pointer group">
                    <RadioGroupItem id={`revenue-${option.value}`} value={option.value} className="size-5 border-border text-primary" />
                    <span className="font-default text-lg text-foreground group-hover:text-primary transition-colors">
                      {option.label}
                    </span>
                  </label>)}
              </RadioGroup>
            </div>
          </div>}

        {currentStep === 3 && <div className="space-y-10">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
                § 01 / 01
              </div>
              <p className="font-default text-2xl md:text-3xl text-foreground leading-snug mb-8">
                How did you <span className="text-primary">DISCOVER</span> Benewealth?
              </p>
              <FormField control={control} name="discovery_source" render={({
            field
          }) => <FormItem>
                    <Select onValueChange={v => {
              field.onChange(v);
              setFormSnapshot(s => ({
                ...s,
                discovery_source: v
              }));
            }} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full bg-transparent border-0 border-b border-border px-0 h-12 text-lg text-foreground focus:ring-0 focus:border-primary rounded-md">
                          <SelectValue placeholder="Select a source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-card border-border rounded-md">
                        {discoverySources.map((src, i) => <SelectItem data-index={i} key={src} value={src}>
                            {src}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="border border-border bg-secondary/10 p-8 md:p-10 rounded-md shadow-md">
              <div className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
                Application Blueprint
              </div>
              <div className="space-y-5">
                {[{
              label: "Principal",
              value: formSnapshot.full_name || "—"
            }, {
              label: "Email",
              value: formSnapshot.email_address || "—"
            }, {
              label: "Entity",
              value: formSnapshot.business_name || "—"
            }, {
              label: "Primary Solution",
              value: formSnapshot.service_interest || "—"
            }, {
              label: "Interests",
              value: formSnapshot.interests || "—"
            }, {
              label: "Revenue",
              value: revenueOptions.find(b => b.value === formSnapshot.revenue)?.label || "—"
            }].map((item, i) => <div data-index={i} key={item.label} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 pb-4 border-b border-border last:border-0 last:pb-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground min-w-[140px]">
                      {item.label}
                    </div>
                    <div className="font-default text-lg text-foreground flex-1">
                      {item.value}
                    </div>
                  </div>)}
              </div>
            </div>

            {submitError && <div className="p-4 border border-destructive bg-destructive/10 rounded-md">
                <p className="font-mono text-sm text-destructive">{submitError}</p>
              </div>}
          </div>}
      </div>

      <div className="mt-16 pt-8 border-t border-border flex items-center justify-between">
        {currentStep > 0 ? <Button type="button" variant="ghost" onClick={goBack} className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-transparent px-0 rounded-md">
            <ArrowLeft className="size-4 mr-2" />
            Previous
          </Button> : <span />}

        {currentStep < steps.length - 1 ? <Button type="button" variant="outline" onClick={goNext} className="font-mono text-xs uppercase tracking-widest border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary px-6 rounded-md shadow-sm">
            Continue
            <ArrowRight className="size-4 ml-2" />
          </Button> : <div className="flex flex-col items-end">
            <div className="text-xs text-muted-foreground text-center mb-4">
              No guaranteed credit score increases, funding approvals, or financial outcomes.
            </div>
            <div className="text-xs text-muted-foreground text-center mb-4">
              By providing your phone number, you agree to receive text messages from Benewealth Financial. Message and data rates may apply. Message frequency varies.
            </div>
            <Button type="submit" disabled={isSubmitting} className="font-mono text-xs uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 h-auto rounded-md shadow-md">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Processing...
                </> : "Submit Application"}
            </Button>
          </div>}
      </div>
    </Form>;
}
export default function TheQualificationProcess() {
  return <section data-section-id="4116" id="the-qualification-process" className="bg-background text-foreground py-20 md:py-32 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 text-center max-w-3xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
            § 04 — Qualification
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] mb-6">
            Architect your <span className="text-primary">WEALTH</span>.
          </h2>
          <p className="font-default text-lg md:text-xl text-muted-foreground italic leading-relaxed">
            A selective process for serious individuals. We do not work with everyone. 
            Complete this initial review to determine eligibility for our elite financial architecture.
          </p>
          <div className="mt-10 flex justify-center">
            <div className="h-px w-16 bg-primary" />
          </div>
        </div>

        <div className="grid grid-cols-12">
          <div className="col-span-12 md:col-start-2 md:col-span-10 lg:col-start-3 lg:col-span-8">
            <FormProvider formKey="qualification_form_qualification_application" formId="qualification_form_qualification_application" sectionName="the-qualification-process" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            business_name: "",
            service_interest: "",
            financial_goals: "",
            discovery_source: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true
            },
            service_interest: {
              required: true
            },
            financial_goals: {
              required: true,
              minLength: 10,
              maxLength: 500
            }
          }} submitText="Submit Application" successMessage="Our analysts will review your profile within 24 hours." errorMessage="Something interrupted the transmission. Please try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}